import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4"></i>
          <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-2">
            文章未找到
          </h1>
          <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.coverImage,
    "author": {
      "@type": "Person",
      "name": article.author,
    },
    "datePublished": article.publishDate,
    "publisher": {
      "@type": "Organization",
      "name": "色戒完整指南",
    },
    "url": `https://cnyangji.cn/article/${article.slug}`,
    "inLanguage": "zh-CN",
    "keywords": article.tags.join(', '),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://cnyangji.cn/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.category,
        "item": `https://cnyangji.cn/category/${article.categorySlug}`,
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50 pt-20">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}></script>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <div className="py-4">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="pb-10">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium hover:bg-accent-200 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              <span className="text-sm text-foreground-400">{article.publishDate}</span>
              <span className="text-sm text-foreground-400">{article.readTime}</span>
              <span className="text-sm text-foreground-400">{article.author}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-1 rounded-md bg-background-100 text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Cover Image */}
          <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: article.content }}
          ></div>

          {/* Tags at Bottom */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签:</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-1 rounded-md bg-background-100 text-foreground-600 cursor-pointer hover:bg-background-200 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="pb-14 border-t border-background-200/70 pt-10">
            <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <ArticleCard key={rel.id} article={rel} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}